/**
 * Central registry of React Query hooks.
 * All server state lives here — components consume via hooks only.
 */
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  Challenge,
  LeaderboardEntry,
  Spot,
  Tournament,
  Training,
  TrainingLevel,
  User,
} from "@/types/api";

// ---------- keys ------------------------------------------------------------
export const qk = {
  me: ["me"] as const,
  tournaments: (filter?: string) =>
    filter ? (["tournaments", filter] as const) : (["tournaments"] as const),
  tournament: (id: number) => ["tournament", id] as const,
  trainings: ["trainings"] as const,
  training: (id: number) => ["training", id] as const,
  spots: ["spots"] as const,
  // Root key — covers every leaderboard variant (infinite pages + /me)
  // so a single invalidate refreshes everything after a rating change.
  leaderboardAll: ["leaderboard"] as const,
  myMatches: ["me", "matches"] as const,
  ratingHistory: ["me", "rating-history"] as const,
  userProfile: (id: number) => ["user-profile", id] as const,
  userProfileAll: ["user-profile"] as const,
};

/** Invalidate every query whose result depends on a user's ELO rating.
 *
 * Called from any mutation that may have triggered a rating recalculation
 * (challenge report, tournament match result, tournament finish). Covers:
 *  • the leaderboard (all variants)
 *  • any open UserProfileSheet
 *  • the current user's own /auth/me cache (if someone keeps it in RQ)
 *  • the match + rating-history lists on the profile page.
 */
function invalidateRatingDerived(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.leaderboardAll });
  void qc.invalidateQueries({ queryKey: qk.userProfileAll });
  void qc.invalidateQueries({ queryKey: qk.me });
  void qc.invalidateQueries({ queryKey: qk.myMatches });
  void qc.invalidateQueries({ queryKey: qk.ratingHistory });
  // Hero rating on HomePage comes from AuthContext, not from RQ — nudge it
  // to refetch /auth/me via the app-wide custom event.
  window.dispatchEvent(new CustomEvent("sp:rating-changed"));
}

// ---------- queries ---------------------------------------------------------
// How often list/detail screens re-pull server state so two clients (e.g.
// admin + a player registering from their phone) stay in sync without a
// manual refresh. Matches the challenges poll cadence.
const EVENT_REFETCH_MS = 10_000;

export function useTournaments() {
  return useQuery({
    queryKey: qk.tournaments(),
    queryFn: () => api.get<Tournament[]>("/tournaments/"),
    refetchInterval: EVENT_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

export function useTournament(id: number | null) {
  return useQuery({
    queryKey: qk.tournament(id ?? -1),
    queryFn: () => api.get<Tournament>(`/tournaments/${id}`),
    enabled: id != null,
    refetchInterval: EVENT_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

export function useTrainings() {
  return useQuery({
    queryKey: qk.trainings,
    queryFn: () => api.get<Training[]>("/trainings"),
    refetchInterval: EVENT_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

export function useTraining(id: number | null) {
  return useQuery({
    queryKey: qk.training(id ?? -1),
    queryFn: () => api.get<Training>(`/trainings/${id}`),
    enabled: id != null,
    refetchInterval: EVENT_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

export function useSpots() {
  return useQuery({
    queryKey: qk.spots,
    queryFn: () => api.get<Spot[]>("/spots/"),
  });
}

/**
 * Paginated leaderboard for the Community page. Works for both browsing
 * ("" search) and filtered lookup — the server searches across ALL
 * registered users and returns absolute ranks, so typing "Петров" finds
 * rank #742 even when only the top 50 are loaded.
 */
export function useLeaderboardInfinite(
  search: string,
  pageSize = 50,
) {
  const trimmed = search.trim();
  return useInfiniteQuery({
    queryKey: [...qk.leaderboardAll, "page", pageSize, trimmed.toLowerCase()] as const,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(pageParam),
      });
      if (trimmed) params.set("search", trimmed);
      return api.get<LeaderboardEntry[]>(`/leaderboard?${params.toString()}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < pageSize ? undefined : allPages.length * pageSize,
    // Keep stale results on screen while a new search is typed — prevents
    // a blank flash between the old query and the new one landing.
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * One-shot search for the PlayerPicker's server-mode — hits the same
 * `/leaderboard` endpoint but as an imperative call so the picker can
 * debounce it internally without coordinating with React Query's cache.
 * Returns up to `limit` players matched against `query` (empty query
 * returns the top-N by rating, which doubles as a "suggested" list).
 */
export function searchPlayers(
  query: string,
  limit = 50,
): Promise<LeaderboardEntry[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  const q = query.trim();
  if (q) params.set("search", q);
  return api.get<LeaderboardEntry[]>(`/leaderboard?${params.toString()}`);
}

export function useMyLeaderboardRank() {
  return useQuery({
    queryKey: [...qk.leaderboardAll, "me"] as const,
    queryFn: () => api.get<LeaderboardEntry>("/leaderboard/me"),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export interface MatchHistoryItem {
  id: number;
  opponent_id: number;
  opponent_username: string | null;
  opponent_first_name: string | null;
  opponent_photo_url: string | null;
  my_score: number;
  opponent_score: number;
  won: boolean;
  rating_change: number;
  played_at: string;
}

// The raw API shape for /users/me/matches — kept internal because it's
// awkward to render (nested `opponent`, `score` as a string).
interface RawMatchHistoryItem {
  id: number;
  opponent: {
    id: number;
    username: string | null;
    first_name: string | null;
    last_name: string | null;
    photo_url: string | null;
  };
  result: "win" | "loss";
  score: string;               // e.g. "11-7"
  date: string;                // ISO
  rating_change: number;
}

function parseScore(score: string, won: boolean): { my: number; opp: number } {
  const [a, b] = score.split(/[-–—]/).map((s) => Number.parseInt(s.trim(), 10) || 0);
  // API gives "winner-loser"; flip when the caller lost.
  return won ? { my: a ?? 0, opp: b ?? 0 } : { my: b ?? 0, opp: a ?? 0 };
}

export function useMyMatches(limit = 10, offset = 0) {
  return useQuery({
    queryKey: [...qk.myMatches, limit, offset],
    queryFn: async () => {
      const raw = await api.get<RawMatchHistoryItem[]>(
        `/users/me/matches?limit=${limit}&offset=${offset}`,
      );
      return raw.map<MatchHistoryItem>((m) => {
        const won = m.result === "win";
        const { my, opp } = parseScore(m.score, won);
        return {
          id: m.id,
          opponent_id: m.opponent.id,
          opponent_username: m.opponent.username,
          opponent_first_name: m.opponent.first_name,
          opponent_photo_url: m.opponent.photo_url,
          my_score: my,
          opponent_score: opp,
          won,
          rating_change: m.rating_change,
          played_at: m.date,
        };
      });
    },
  });
}

// "Мои турниры" — история регистраций. Последние сверху.
export type MyTournamentResult =
  | "winner"
  | "finalist"
  | "participant"
  | "in_progress"
  | "registered"
  | null;

export interface MyTournamentItem {
  id: number;
  name: string;
  status: string;
  result: MyTournamentResult;
  date: string;             // ISO
  spot_name: string | null;
  description?: string | null;
}

export function useMyTournaments(limit = 10, offset = 0) {
  return useQuery({
    queryKey: ["me", "tournaments", limit, offset] as const,
    queryFn: () =>
      api.get<MyTournamentItem[]>(
        `/users/me/tournaments?limit=${limit}&offset=${offset}`,
      ),
  });
}

export interface RatingPoint {
  date: string;
  rating: number;
}
export function useRatingHistory() {
  return useQuery({
    queryKey: qk.ratingHistory,
    queryFn: () => api.get<RatingPoint[]>("/users/me/rating-history"),
  });
}

// ---------- user profile sheet --------------------------------------------
export interface PublicUserProfile {
  id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  rating: number;
  is_admin: boolean;
  is_trainer: boolean;
  wins: number;
  losses: number;
  matches_played: number;
  rating_history: { date: string; rating: number }[];
}

export function usePublicUserProfile(userId: number | null) {
  return useQuery({
    queryKey: qk.userProfile(userId ?? -1),
    queryFn: () => api.get<PublicUserProfile>(`/users/by-id/${userId}/profile`),
    enabled: userId != null,
    // Sheet is usually open a few seconds; keep the chart + W/L + rating
    // live while it's mounted.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
}

export function useUser(username: string | null) {
  return useQuery({
    queryKey: ["user", username],
    queryFn: () => api.get<User>(`/users/${username}`),
    enabled: !!username,
  });
}

// ---------- mutations -------------------------------------------------------

/**
 * Optimistically flip `is_registered` + adjust `registered_count` on the
 * cached tournament detail and the tournaments list so the UI swaps the
 * action button instantly, without the network round-trip flicker.
 * Returns a rollback snapshot for onError.
 */
function applyOptimisticRegistration(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
  registering: boolean,
) {
  const detailKey = qk.tournament(id);
  const listKey = qk.tournaments();

  const prevDetail = qc.getQueryData<Tournament>(detailKey);
  const prevList = qc.getQueryData<Tournament[]>(listKey);

  const patch = (t: Tournament): Tournament => ({
    ...t,
    is_registered: registering,
    registered_count: Math.max(0, t.registered_count + (registering ? 1 : -1)),
  });

  if (prevDetail) qc.setQueryData<Tournament>(detailKey, patch(prevDetail));
  if (prevList) {
    qc.setQueryData<Tournament[]>(
      listKey,
      prevList.map((t) => (t.id === id ? patch(t) : t)),
    );
  }

  return { prevDetail, prevList, detailKey, listKey };
}

export function useRegisterTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tournamentId: number) =>
      api.post<void>(`/tournaments/${tournamentId}/register`),
    onMutate: (id) => applyOptimisticRegistration(qc, id, true),
    onError: (_err, _id, ctx) => {
      if (!ctx) return;
      if (ctx.prevDetail) qc.setQueryData(ctx.detailKey, ctx.prevDetail);
      if (ctx.prevList) qc.setQueryData(ctx.listKey, ctx.prevList);
    },
    onSettled: (_data, _err, id) => {
      void qc.invalidateQueries({ queryKey: qk.tournament(id) });
      void qc.invalidateQueries({ queryKey: qk.tournaments() });
    },
  });
}

export function useUnregisterTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tournamentId: number) =>
      api.delete<void>(`/tournaments/${tournamentId}/unregister`),
    onMutate: (id) => applyOptimisticRegistration(qc, id, false),
    onError: (_err, _id, ctx) => {
      if (!ctx) return;
      if (ctx.prevDetail) qc.setQueryData(ctx.detailKey, ctx.prevDetail);
      if (ctx.prevList) qc.setQueryData(ctx.listKey, ctx.prevList);
    },
    onSettled: (_data, _err, id) => {
      void qc.invalidateQueries({ queryKey: qk.tournament(id) });
      void qc.invalidateQueries({ queryKey: qk.tournaments() });
    },
  });
}

/**
 * Mirror of {@link applyOptimisticRegistration} for trainings — keeps
 * `is_registered` and `registered_count` in sync on both the detail cache
 * and the list cache so the button flips instantly.
 */
function applyOptimisticTrainingRegistration(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
  registering: boolean,
) {
  const detailKey = qk.training(id);
  const listKey = qk.trainings;

  const prevDetail = qc.getQueryData<Training>(detailKey);
  const prevList = qc.getQueryData<Training[]>(listKey);

  const patch = (t: Training): Training => ({
    ...t,
    is_registered: registering,
    registered_count: Math.max(0, (t.registered_count ?? 0) + (registering ? 1 : -1)),
  });

  if (prevDetail) qc.setQueryData<Training>(detailKey, patch(prevDetail));
  if (prevList) {
    qc.setQueryData<Training[]>(
      listKey,
      prevList.map((t) => (t.id === id ? patch(t) : t)),
    );
  }

  return { prevDetail, prevList, detailKey, listKey };
}

export function useRegisterTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trainingId: number) =>
      api.post<void>(`/trainings/${trainingId}/register`),
    onMutate: (id) => applyOptimisticTrainingRegistration(qc, id, true),
    onError: (_err, _id, ctx) => {
      if (!ctx) return;
      if (ctx.prevDetail) qc.setQueryData(ctx.detailKey, ctx.prevDetail);
      if (ctx.prevList) qc.setQueryData(ctx.listKey, ctx.prevList);
    },
    onSettled: (_data, _err, id) => {
      void qc.invalidateQueries({ queryKey: qk.training(id) });
      void qc.invalidateQueries({ queryKey: qk.trainings });
    },
  });
}

export function useUnregisterTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trainingId: number) =>
      api.delete<void>(`/trainings/${trainingId}/register`),
    onMutate: (id) => applyOptimisticTrainingRegistration(qc, id, false),
    onError: (_err, _id, ctx) => {
      if (!ctx) return;
      if (ctx.prevDetail) qc.setQueryData(ctx.detailKey, ctx.prevDetail);
      if (ctx.prevList) qc.setQueryData(ctx.listKey, ctx.prevList);
    },
    onSettled: (_data, _err, id) => {
      void qc.invalidateQueries({ queryKey: qk.training(id) });
      void qc.invalidateQueries({ queryKey: qk.trainings });
    },
  });
}

export function useNotifyTrainingUpdate() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ message: string }>(`/trainings/${id}/notify-update`),
  });
}

// ---------- training authoring (trainers + admins) ------------------------
export type { TrainingLevel } from "@/types/api";

export interface TrainingCreatePayload {
  title: string;
  description?: string | null;
  spot_id: number;
  date_time: string;
  duration: number;
  max_participants: number;
  price: number;
  level: TrainingLevel;
}

export function useCreateTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TrainingCreatePayload) =>
      api.post<Training>("/trainings", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.trainings });
    },
  });
}

export function useUpdateTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; patch: TrainingCreatePayload }) =>
      api.put<Training>(`/trainings/${args.id}`, args.patch),
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: qk.trainings });
      void qc.invalidateQueries({ queryKey: qk.training(args.id) });
    },
  });
}

export function useCancelTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.put<void>(`/trainings/${id}/cancel`),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: qk.trainings });
      void qc.invalidateQueries({ queryKey: qk.training(id) });
    },
  });
}

// ---------- admin mutations ------------------------------------------------
export type TournamentFormat = "single_elim" | "double_elim" | "round_robin";
export type SeedingMode = "rating" | "random" | "manual";

export interface TournamentCreatePayload {
  name: string;
  description?: string | null;
  spot_id: number;
  date_time: string;     // ISO
  max_participants: number;
  entry_fee: number;
  format?: TournamentFormat;
  team_size?: 1 | 2;
  seeding_mode?: SeedingMode;
  best_of?: number;
}

export function useCreateTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TournamentCreatePayload) =>
      api.post<Tournament>("/tournaments/", payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.tournaments() }); },
  });
}

export function useDeleteTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/tournaments/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.tournaments() }); },
  });
}

export interface TournamentUpdatePayload {
  name?: string;
  description?: string | null;
  spot_id?: number;
  date_time?: string;
  max_participants?: number;
  entry_fee?: number;
  format?: TournamentFormat;
  team_size?: 1 | 2;
  seeding_mode?: SeedingMode;
  best_of?: number;
}

export function useUpdateTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; patch: TournamentUpdatePayload }) =>
      api.put<Tournament>(`/tournaments/${args.id}`, args.patch),
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: qk.tournaments() });
      void qc.invalidateQueries({ queryKey: qk.tournament(args.id) });
    },
  });
}

export function useCancelTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.put<void>(`/tournaments/${id}/cancel`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.tournaments() }); },
  });
}

export interface SpotCreatePayload {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  place_type: "outdoor_spot" | "indoor_hall" | "store";
  net_type?: "metal" | "regular" | "none" | null;
  has_roof?: boolean | null;
  tables_count?: number | null;
  website?: string | null;
  phone?: string | null;
}

export function useCreateSpot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SpotCreatePayload) => api.post<Spot>("/spots/", payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.spots }); },
  });
}

export function useDeleteSpot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/spots/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.spots }); },
  });
}

export function useSpot(id: number | null) {
  return useQuery({
    queryKey: ["spot", id ?? -1],
    queryFn: () => api.get<Spot>(`/spots/${id}`),
    enabled: id != null,
  });
}

export function useUpdateSpot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; patch: Partial<SpotCreatePayload> }) =>
      api.put<Spot>(`/spots/${args.id}`, args.patch),
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: qk.spots });
      void qc.invalidateQueries({ queryKey: ["spot", args.id] });
    },
  });
}

// ---------- admin: users ---------------------------------------------------
export interface AdminUser extends User {
  is_registered: boolean;
  created_at: string | null;
}

const ADMIN_USERS_KEY = ["admin", "users"] as const;

export function useAdminUsers(search: string = "") {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, search],
    queryFn: () => {
      const q = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : "";
      return api.get<AdminUser[]>(`/admin/users${q}`);
    },
  });
}

export function useBanUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/admin/users/${id}/ban`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }); },
  });
}

export function useUnbanUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/admin/users/${id}/unban`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }); },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/admin/users/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }); },
  });
}

export function usePromoteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/admin/users/${id}/promote`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }); },
  });
}

export function useDemoteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/admin/users/${id}/demote`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }); },
  });
}

export function usePromoteTrainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/admin/users/${id}/promote-trainer`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }); },
  });
}

export function useDemoteTrainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/admin/users/${id}/demote-trainer`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }); },
  });
}

// ---------- active match (player-facing) ----------------------------------
export interface ActiveMatch {
  tournament_id: number;
  tournament_name: string;
  best_of: number;
  match: BracketMatch;
  my_team_id: number;
  opponent_team_id: number | null;
  reported_by_me: boolean;
  reported_winner_team_id: number | null;
  opponent_reported: boolean;
  opponent_reported_winner_team_id: number | null;
}

export function useMyActiveMatch() {
  return useQuery({
    queryKey: ["me", "active-match"] as const,
    queryFn: () => api.get<ActiveMatch | null>("/tournaments/me/active-match"),
    // Match state flips when the other team reports or an admin writes a
    // result — poll so the home screen updates without refresh.
    refetchInterval: EVENT_REFETCH_MS,
    refetchOnWindowFocus: true,
    staleTime: 3_000,
  });
}


// ---------- internal bracket (T-1) ------------------------------------------
export interface BracketTeamMember {
  user_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  rating: number | null;
  // Admin-added placeholder participant without a Telegram account.
  // Rendered with a "гость" badge in admin views only — regular players
  // see guests as ordinary participants.
  is_guest: boolean;
}

export interface BracketTeam {
  id: number;
  name: string | null;
  seed: number | null;
  is_bye: boolean;
  members: BracketTeamMember[];
}

export interface BracketGame {
  number: number;
  a: number;
  b: number;
}

export interface BracketMatch {
  id: number;
  round: number;
  slot: number;
  bracket: "upper" | "lower" | "grand_final";
  state: "pending" | "ready" | "waiting_confirm" | "confirmed" | "walkover" | "disputed";
  team_a: BracketTeam | null;
  team_b: BracketTeam | null;
  winner_team_id: number | null;
  next_match_id: number | null;
  games: BracketGame[];
  // Timestamp set by admin "Play" press. The match is "active now" when
  // this is set and the match hasn't been resolved yet — used to light
  // up its card/row so the admin knows which game is in progress.
  started_at: string | null;
}

/** Admin pressed Play and a winner hasn't been recorded yet → match is live. */
export function isMatchLive(m: BracketMatch): boolean {
  if (!m.started_at) return false;
  return m.state === "ready" || m.state === "waiting_confirm" || m.state === "disputed";
}

const BRACKET_KEYS = {
  teams: (id: number) => ["bracket", "teams", id] as const,
  matches: (id: number) => ["bracket", "matches", id] as const,
  standings: (id: number) => ["bracket", "standings", id] as const,
};

export interface StandingPlayer {
  user_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  rating: number;
  wins: number;
  losses: number;
  played: number;
  is_favorite: boolean;
  place: number | null;
}

export interface PodiumMember {
  user_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  rating: number;
}

export interface PodiumEntry {
  place: number;
  team_id: number;
  team_name: string | null;
  wins: number;
  losses: number;
  members: PodiumMember[];
}

export interface TournamentStandings {
  status: string;
  participants: StandingPlayer[];
  podium: PodiumEntry[];
}

export function useTournamentStandings(tournamentId: number | null) {
  return useQuery({
    queryKey: BRACKET_KEYS.standings(tournamentId ?? -1),
    queryFn: () =>
      api.get<TournamentStandings>(`/tournaments/${tournamentId}/standings`),
    enabled: tournamentId != null,
    refetchInterval: EVENT_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

export function useBracketTeams(tournamentId: number) {
  return useQuery({
    queryKey: BRACKET_KEYS.teams(tournamentId),
    queryFn: () => api.get<BracketTeam[]>(`/tournaments/${tournamentId}/teams`),
    refetchInterval: EVENT_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

export function useBracketMatches(tournamentId: number) {
  return useQuery({
    queryKey: BRACKET_KEYS.matches(tournamentId),
    queryFn: () => api.get<BracketMatch[]>(`/tournaments/${tournamentId}/matches`),
    refetchInterval: EVENT_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

export function useAddTeam(tournamentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      user_ids?: number[];
      guest_names?: string[];
      name?: string;
      is_bye?: boolean;
      seed?: number;
    }) => api.post<BracketTeam>(`/tournaments/${tournamentId}/teams`, payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: BRACKET_KEYS.teams(tournamentId) }); },
  });
}

export function useRemoveTeam(tournamentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: number) => api.delete<void>(`/tournaments/${tournamentId}/teams/${teamId}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: BRACKET_KEYS.teams(tournamentId) }); },
  });
}

export function useStartInternalTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.put<{ status: string; rounds: number; matches: number }>(
        `/tournaments/${id}/internal-start`,
      ),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: qk.tournaments() });
      void qc.invalidateQueries({ queryKey: qk.tournament(id) });
      void qc.invalidateQueries({ queryKey: BRACKET_KEYS.matches(id) });
    },
  });
}

export function useAdminMatchResult(tournamentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      matchId: number;
      winner_team_id: number;
      games?: { a: number; b: number }[];
      rewrite?: boolean;
    }) =>
      api.put<BracketMatch>(
        `/tournaments/${tournamentId}/matches/${args.matchId}/admin-result`,
        {
          winner_team_id: args.winner_team_id,
          games: args.games,
          rewrite: args.rewrite ?? false,
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BRACKET_KEYS.matches(tournamentId) });
      // Match result may auto-complete the tournament — refresh status.
      void qc.invalidateQueries({ queryKey: qk.tournament(tournamentId) });
      void qc.invalidateQueries({ queryKey: qk.tournaments() });
      invalidateRatingDerived(qc);
    },
  });
}

export function useAdminPlayMatch(tournamentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId: number) =>
      api.put<{ notified: number }>(
        `/tournaments/${tournamentId}/matches/${matchId}/admin-play`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BRACKET_KEYS.matches(tournamentId) });
    },
  });
}

export function useFinishInternalTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.put<{ status: string }>(`/tournaments/${id}/internal-finish`),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: qk.tournaments() });
      void qc.invalidateQueries({ queryKey: qk.tournament(id) });
      void qc.invalidateQueries({ queryKey: BRACKET_KEYS.matches(id) });
      invalidateRatingDerived(qc);
    },
  });
}

export function useNotifyTournamentUpdate() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ message: string }>(`/tournaments/${id}/notify-update`),
  });
}

// ---------- doubles invites -------------------------------------------------
export interface InviteUser {
  id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  rating: number | null;
}

export interface TournamentInvite {
  id: number;
  tournament_id: number;
  tournament_name: string;
  team_id: number;
  team_name: string | null;
  inviter: InviteUser;
  invitee: InviteUser;
  status: "pending" | "accepted" | "declined" | "cancelled" | "expired";
  created_at: string;
}

const INVITE_KEYS = {
  mine: ["invites", "my"] as const,
  forTournament: (id: number) => ["invites", "tournament", id] as const,
};

export function useMyInvites(enabled = true) {
  return useQuery({
    queryKey: INVITE_KEYS.mine,
    queryFn: () => api.get<TournamentInvite[]>(`/invites/my`),
    enabled,
  });
}

export function useTournamentInvites(tournamentId: number | null) {
  return useQuery({
    queryKey: INVITE_KEYS.forTournament(tournamentId ?? -1),
    queryFn: () => api.get<TournamentInvite[]>(`/tournaments/${tournamentId}/invites`),
    enabled: tournamentId != null,
  });
}

function invalidateInviteQueries(qc: ReturnType<typeof useQueryClient>, tournamentId?: number) {
  void qc.invalidateQueries({ queryKey: INVITE_KEYS.mine });
  if (tournamentId != null) {
    void qc.invalidateQueries({ queryKey: INVITE_KEYS.forTournament(tournamentId) });
    void qc.invalidateQueries({ queryKey: BRACKET_KEYS.teams(tournamentId) });
    void qc.invalidateQueries({ queryKey: qk.tournament(tournamentId) });
    void qc.invalidateQueries({ queryKey: qk.tournaments() });
  }
}

export function useInvitePartner(tournamentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { partner_user_id: number; team_name?: string }) =>
      api.post<TournamentInvite>(
        `/tournaments/${tournamentId}/invite-partner`,
        args,
      ),
    onSuccess: () => invalidateInviteQueries(qc, tournamentId),
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: number) =>
      api.post<TournamentInvite>(`/invites/${inviteId}/accept`),
    onSuccess: (inv) => invalidateInviteQueries(qc, inv.tournament_id),
  });
}

export function useDeclineInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: number) =>
      api.post<TournamentInvite>(`/invites/${inviteId}/decline`),
    onSuccess: (inv) => invalidateInviteQueries(qc, inv.tournament_id),
  });
}

export function useCancelInvite(tournamentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: number) =>
      api.delete<{ cancelled: number }>(`/invites/${inviteId}`),
    onSuccess: () => invalidateInviteQueries(qc, tournamentId),
  });
}

// ---------- challenges -----------------------------------------------------
const CHALLENGE_KEYS = {
  all: ["challenges"] as const,
  mine: ["challenges", "mine"] as const,
};

// Challenges change out of band — opponent accepts, reports a result,
// cancels — so we poll lightly while the tab is active. Anything the
// current user does still gets an instant update via invalidation.
const CHALLENGE_REFETCH_MS = 10_000;

export function useChallenges() {
  return useQuery({
    queryKey: CHALLENGE_KEYS.all,
    queryFn: () => api.get<Challenge[]>("/challenges/"),
    refetchInterval: CHALLENGE_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

export function useMyChallenges() {
  return useQuery({
    queryKey: CHALLENGE_KEYS.mine,
    queryFn: () => api.get<Challenge[]>("/challenges/?mine=true"),
    refetchInterval: CHALLENGE_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
}

function invalidateChallenges(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: CHALLENGE_KEYS.all });
  void qc.invalidateQueries({ queryKey: CHALLENGE_KEYS.mine });
}

export interface CreateChallengePayload {
  challenged_user_id: number;
  // ISO string or null (= play now).
  scheduled_at: string | null;
  // NULL means "players figure it out themselves".
  spot_id: number | null;
}

export function useCreateChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateChallengePayload) =>
      api.post<Challenge>("/challenges/", payload),
    onSuccess: () => invalidateChallenges(qc),
  });
}

export function useAcceptChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Challenge>(`/challenges/${id}/accept`),
    onSuccess: () => invalidateChallenges(qc),
  });
}

export function useDeclineChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Challenge>(`/challenges/${id}/decline`),
    onSuccess: () => invalidateChallenges(qc),
  });
}

export function useCancelChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Challenge>(`/challenges/${id}/cancel`),
    onSuccess: () => invalidateChallenges(qc),
  });
}

export function useReportChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; winner_user_id: number }) =>
      api.post<Challenge>(`/challenges/${args.id}/report`, {
        winner_user_id: args.winner_user_id,
      }),
    onSuccess: () => {
      invalidateChallenges(qc);
      invalidateRatingDerived(qc);
    },
  });
}

export function useReportMatch(tournamentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      matchId: number;
      winner_team_id: number;
      games?: { a: number; b: number }[];
    }) =>
      api.post<BracketMatch>(
        `/tournaments/${tournamentId}/matches/${args.matchId}/report`,
        { winner_team_id: args.winner_team_id, games: args.games },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BRACKET_KEYS.matches(tournamentId) });
      void qc.invalidateQueries({ queryKey: ["me", "active-match"] });
      invalidateRatingDerived(qc);
    },
  });
}

// ---------- admin: broadcast --------------------------------------------------
export interface BroadcastResult {
  queued: number;
  has_image: boolean;
}

export function useBroadcastToBot() {
  return useMutation({
    mutationFn: (args: { text: string; image?: File | null }) => {
      const fd = new FormData();
      fd.append("text", args.text);
      if (args.image) fd.append("image", args.image);
      // Larger timeout: image upload may take a moment.
      return api.post<BroadcastResult>("/admin/broadcasts", fd, { timeoutMs: 60_000 });
    },
  });
}

// ---------- admin proxy -----------------------------------------------------

export type ProxyStatus = "ok" | "fail" | "checking" | "disabled";

export interface ProxyConfig {
  url: string | null;
  enabled: boolean;
  status: ProxyStatus;
  last_checked_at: string | null;
  last_error: string | null;
  updated_at: string;
}

const PROXY_KEY = ["admin", "proxy"] as const;

export function useProxyConfig(enabled = true) {
  return useQuery({
    queryKey: PROXY_KEY,
    queryFn: () => api.get<ProxyConfig>("/admin/proxy"),
    enabled,
    // The menu dot is the main consumer — keep it close to live without
    // hammering the endpoint. The server-side scheduler probes every
    // 5 min; a 30s refetch is cheap and feels responsive.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useUpdateProxyConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { url: string | null; enabled: boolean }) =>
      api.put<ProxyConfig>("/admin/proxy", body),
    onSuccess: (data) => qc.setQueryData(PROXY_KEY, data),
  });
}

export function useTestProxyConfig() {
  const qc = useQueryClient();
  return useMutation({
    // Probe takes up to ~15s server-side; give the client a generous
    // timeout so a slow Telegram/proxy doesn't get misreported as a
    // frontend error.
    mutationFn: (body: { url?: string | null }) =>
      api.post<ProxyConfig>("/admin/proxy/test", body, { timeoutMs: 20_000 }),
    onSuccess: (data) => qc.setQueryData(PROXY_KEY, data),
  });
}
